/**
 * Search query → JMAP filter.
 *
 * The search box advertises `from:helena, has:attachment, is:unread`, so those
 * operators have to do something — shipping a placeholder that promises syntax
 * the server never sees is worse than no placeholder. Everything that is not an
 * operator falls through to a full-text `text` condition.
 *
 * RFC 8621 FilterCondition fields used here: from, to, subject, body, text,
 * hasAttachment, hasKeyword, notKeyword, before, after.
 */

export interface FilterCondition {
  [key: string]: unknown;
}

/** `is:` values that map onto a keyword rather than a field. */
const KEYWORD_FLAGS: Record<string, { keyword: string; present: boolean }> = {
  unread: { keyword: "$seen", present: false },
  read: { keyword: "$seen", present: true },
  starred: { keyword: "$flagged", present: true },
  flagged: { keyword: "$flagged", present: true },
  unstarred: { keyword: "$flagged", present: false },
  draft: { keyword: "$draft", present: true },
};

/**
 * Splits on whitespace but keeps "quoted phrases" together, including when the
 * quotes follow an operator.
 *
 * The `\S*` prefix matters: without it the bare-token branch matches
 * `subject:"deploy` up to the space and the quoted branch never gets a look in,
 * so `subject:"deploy runbook"` searched subjects for "deploy" and then full
 * text for a stray `runbook"`.
 */
function tokenize(query: string): string[] {
  return query.match(/\S*"[^"]*"|\S+/g) ?? [];
}

function unquote(value: string): string {
  return value.replace(/^"|"$/g, "");
}

/**
 * Builds the conditions for a query. Returns an empty array for a blank query,
 * which callers should treat as "no filtering beyond the mailbox".
 */
export function parseSearch(query: string): FilterCondition[] {
  const conditions: FilterCondition[] = [];
  const free: string[] = [];

  for (const token of tokenize(query.trim())) {
    const match = token.match(/^(from|to|cc|subject|body|has|is|before|after):(.*)$/i);
    if (!match) {
      free.push(unquote(token));
      continue;
    }

    const field = match[1].toLowerCase();
    const value = unquote(match[2]).trim();
    if (!value) continue;

    switch (field) {
      case "from":
      case "to":
      case "cc":
      case "subject":
      case "body":
        conditions.push({ [field]: value });
        break;

      case "has":
        // `has:attachment` is the only meaningful form; anything else is more
        // useful as free text than silently dropped.
        if (/^attachments?$/i.test(value)) conditions.push({ hasAttachment: true });
        else free.push(token);
        break;

      case "is": {
        const flag = KEYWORD_FLAGS[value.toLowerCase()];
        if (flag) conditions.push(flag.present ? { hasKeyword: flag.keyword } : { notKeyword: flag.keyword });
        else free.push(token);
        break;
      }

      case "before":
      case "after": {
        // JMAP wants UTCDate; a bare YYYY-MM-DD is what people type.
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) free.push(token);
        else conditions.push({ [field]: date.toISOString() });
        break;
      }
    }
  }

  if (free.length) conditions.push({ text: free.join(" ") });
  return conditions;
}
